import React, { useState, useEffect } from "react";
import { createRoot, Root } from "react-dom/client";
import IMask from "imask";

interface PopupFormProps {
  formId: string;
  triggerId?: string;
  title?: string;
}

type SubmitStatus = "success" | "error" | null;

/**
 * Компонент popup формы
 * Использует Progressive Enhancement:
 * - Hugo генерирует форму в HTML (SEO-friendly, доступна роботам)
 * - React скрывает форму и показывает по клику
 */
function PopupForm({
  formId,
  triggerId,
  title,
}: PopupFormProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const [formContent, setFormContent] = useState<HTMLElement | null>(null);

  const openPopup = (): void => {
    setIsOpen(true);
    document.body.style.overflow = "hidden"; // Блокируем скролл
  };

  const closePopup = (): void => {
    setIsOpen(false);
    document.body.style.overflow = ""; // Восстанавливаем скролл
  };

  useEffect(() => {
    setIsMounted(true);
    // Находим оригинальную форму в DOM
    const originalForm = document.getElementById(formId);
    if (originalForm) {
      // Скрываем оригинальную форму (она уже в HTML для SEO)
      originalForm.style.display = "none";
    }

    // Находим кнопки-триггеры и привязываем обработчики
    // Ищем кнопки по ID (если указан triggerId) и по data-атрибуту (для всех кнопок с data-trigger-for)
    const selectors: string[] = [];
    if (triggerId) {
      selectors.push(
        `#${triggerId}`,
        `#${triggerId}-bottom`,
        `[id^="${triggerId}"]`
      );
    }
    // Всегда ищем кнопки с data-trigger-for для этой формы
    selectors.push(`[data-trigger-for="${formId}"]`);

    const triggerButtons = document.querySelectorAll<HTMLElement>(
      selectors.join(", ")
    );

    const handleClick = (e: Event): void => {
      e.preventDefault();
      e.stopPropagation();
      openPopup();
    };

    triggerButtons.forEach((button) => {
      // Пропускаем кнопки, которые уже обрабатываются DynamicForm
      if (
        button.dataset.dynamicFormInitialized === "true" ||
        button.dataset.reactInitialized === "true"
      ) {
        return;
      }
      button.addEventListener("click", handleClick);
      button.dataset.popupFormInitialized = "true";
    });

    return () => {
      triggerButtons.forEach((button) => {
        button.removeEventListener("click", handleClick);
        delete button.dataset.popupFormInitialized;
      });
    };
  }, [formId, triggerId]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && isOpen) {
        closePopup();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Закрытие по клику вне формы
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  useEffect(() => {
    const originalForm = document.getElementById(
      formId
    ) as HTMLFormElement | null;
    if (originalForm) {
      // Клонируем форму, чтобы сохранить все атрибуты и валидацию
      const clonedForm = originalForm.cloneNode(true) as HTMLFormElement;
      clonedForm.style.display = "block";
      clonedForm.id = `${formId}-react`;

      // Обработчик отправки формы
      const handleSubmit = async (e: Event): Promise<void> => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        // Отключаем кнопку отправки
        const submitButton = clonedForm.querySelector<HTMLButtonElement>(
          'button[type="submit"]'
        );
        if (submitButton) {
          submitButton.disabled = true;
        }

        const formData = new FormData(clonedForm);

        // Проверяем согласие на обработку персональных данных
        const consentCheckbox = clonedForm.querySelector<HTMLInputElement>(
          'input[name="consent"]'
        );
        if (!consentCheckbox || !consentCheckbox.checked) {
          setSubmitStatus("error");
          const messageDiv = clonedForm.querySelector<HTMLElement>(
            `#${formId}-message`
          );
          if (messageDiv) {
            messageDiv.className =
              "p-3 bg-red-50 border border-red-200 rounded-lg";
            messageDiv.innerHTML =
              '<p class="text-red-800 text-sm">Необходимо дать согласие на обработку персональных данных</p>';
            messageDiv.classList.remove("hidden");
          }
          setIsSubmitting(false);
          if (submitButton) {
            submitButton.disabled = false;
          }
          return;
        }

        try {
          // Отправляем форму на PHP скрипт
          const response = await fetch("/form.php", {
            method: "POST",
            body: formData,
          });

          const result = await response.json();

          if (result.success) {
            setSubmitStatus("success");

            // Очищаем форму
            clonedForm.reset();

            // Обновляем CAPTCHA
            const captchaImage = clonedForm.querySelector<HTMLImageElement>(
              `#${formId}-captcha-image`
            );
            if (captchaImage) {
              captchaImage.src = `/captcha.php?${Math.random()}`;
            }

            // Скрываем сообщение об ошибке, если было
            const messageDiv = clonedForm.querySelector<HTMLElement>(
              `#${formId}-message`
            );
            if (messageDiv) {
              messageDiv.classList.add("hidden");
            }

            // Закрываем popup через 3 секунды после успешной отправки
            setTimeout(() => {
              closePopup();
              setSubmitStatus(null);
              if (submitButton) {
                submitButton.disabled = false;
              }
            }, 3000);
          } else {
            setSubmitStatus("error");
            const messageDiv = clonedForm.querySelector<HTMLElement>(
              `#${formId}-message`
            );
            if (messageDiv) {
              messageDiv.className =
                "p-3 bg-red-50 border border-red-200 rounded-lg";
              messageDiv.innerHTML = `<p class="text-red-800 text-sm">${result.message}</p>`;
              messageDiv.classList.remove("hidden");
            }

            // Обновляем CAPTCHA при ошибке
            const captchaImage = clonedForm.querySelector<HTMLImageElement>(
              `#${formId}-captcha-image`
            );
            if (captchaImage) {
              captchaImage.src = `/captcha.php?${Math.random()}`;
            }

            // Очищаем поле CAPTCHA
            const captchaInput = clonedForm.querySelector<HTMLInputElement>(
              `#${formId}-captcha`
            );
            if (captchaInput) {
              captchaInput.value = "";
            }
          }
        } catch (error) {
          console.error("Form submission error:", error);
          setSubmitStatus("error");
          const messageDiv = clonedForm.querySelector<HTMLElement>(
            `#${formId}-message`
          );
          if (messageDiv) {
            messageDiv.className =
              "p-3 bg-red-50 border border-red-200 rounded-lg";
            messageDiv.innerHTML =
              '<p class="text-red-800 text-sm">Произошла ошибка при отправке. Пожалуйста, попробуйте позже или позвоните нам напрямую.</p>';
            messageDiv.classList.remove("hidden");
          }
        } finally {
          setIsSubmitting(false);
          if (submitButton) {
            submitButton.disabled = false;
          }
        }
      };

      clonedForm.addEventListener("submit", handleSubmit);

      // Обработчик обновления CAPTCHA
      const captchaImage = clonedForm.querySelector<HTMLImageElement>(
        `#${formId}-captcha-image`
      );
      if (captchaImage) {
        captchaImage.addEventListener("click", () => {
          captchaImage.src = `/captcha.php?${Math.random()}`;
        });
      }

      // Инициализация маски телефона с использованием imask
      const phoneInput = clonedForm.querySelector<HTMLInputElement>(
        `#${formId}-phone, input[name="phone"]`
      );
      if (phoneInput) {
        // Создаем маску для российского формата телефона
        const mask = IMask(phoneInput, {
          mask: "+7 (000) 000-00-00",
          lazy: false, // Показываем маску сразу
          placeholderChar: "_", // Символ-плейсхолдер
        });

        // Обработка фокуса
        phoneInput.addEventListener("focus", () => {
          if (
            !phoneInput.value ||
            phoneInput.value.trim() === "" ||
            phoneInput.value === "+7 (___) ___-__-__"
          ) {
            mask.value = "+7";
            phoneInput.setSelectionRange(2, 2);
          }
        });

        // Обработка blur
        phoneInput.addEventListener("blur", () => {
          const cleanValue = phoneInput.value.replace(/\D/g, "");
          if (
            cleanValue === "7" ||
            cleanValue === "" ||
            phoneInput.value === "+7" ||
            phoneInput.value === "+7 (___) ___-__-__"
          ) {
            mask.value = "";
          }
        });

        // Сохраняем ссылку на маску
        (phoneInput as any).imask = mask;
      }

      // Управление показом CAPTCHA
      const captchaContainer = clonedForm.querySelector<HTMLElement>(
        `#${formId}-captcha-container`
      );
      if (captchaContainer) {
        const requiredFields = clonedForm.querySelectorAll<HTMLInputElement>(
          'input[required]:not([name="captcha"]):not([name="consent"])'
        );
        const consentCheckbox = clonedForm.querySelector<HTMLInputElement>(
          'input[name="consent"]'
        );

        const checkFields = (): void => {
          let allFilled = true;
          requiredFields.forEach((field) => {
            const value = field.value.trim();
            if (!value || (field.type === "tel" && value.length < 10)) {
              allFilled = false;
            }
          });
          if (consentCheckbox && !consentCheckbox.checked) {
            allFilled = false;
          }
          if (allFilled) {
            captchaContainer.classList.remove("hidden");
            const captchaInput = clonedForm.querySelector<HTMLInputElement>(
              'input[name="captcha"]'
            );
            if (captchaInput) {
              captchaInput.required = true;
            }
          } else {
            captchaContainer.classList.add("hidden");
            const captchaInput = clonedForm.querySelector<HTMLInputElement>(
              'input[name="captcha"]'
            );
            if (captchaInput) {
              captchaInput.required = false;
              captchaInput.value = "";
            }
          }
        };

        requiredFields.forEach((field) => {
          field.addEventListener("input", checkFields);
          field.addEventListener("change", checkFields);
        });
        if (consentCheckbox) {
          consentCheckbox.addEventListener("change", checkFields);
        }
        checkFields();
      }

      setFormContent(clonedForm);

      return () => {
        clonedForm.removeEventListener("submit", handleSubmit);
      };
    }
  }, [formId]);

  if (!isMounted) {
    return null;
  }

  return (
    <>
      {/* Popup overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {title || "Записаться на приём"}
              </h2>
              <button
                onClick={closePopup}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Form content */}
            <div className="p-6">
              {formContent && (
                <>
                  <div
                    dangerouslySetInnerHTML={{ __html: formContent.outerHTML }}
                  />

                  {/* Статус отправки */}
                  {submitStatus === "success" && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-green-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                        <p className="text-green-800 text-sm font-medium">
                          Спасибо! Ваша заявка принята. Мы свяжемся с вами в
                          ближайшее время.
                        </p>
                      </div>
                    </div>
                  )}

                  {submitStatus === "error" && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm font-medium">
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        Произошла ошибка. Пожалуйста, попробуйте позже или
                        позвоните нам напрямую.
                      </p>
                    </div>
                  )}

                  {isSubmitting && (
                    <div className="mt-4 text-center">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <p className="text-gray-600 text-sm mt-2">Отправка...</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Хранилище для уже созданных roots
const rootsMap = new WeakMap<HTMLElement, Root>();

/**
 * Инициализация popup форм
 * Ищет элементы с data-popup-form атрибутом
 */
export function initPopupForms(): void {
  const popupContainers =
    document.querySelectorAll<HTMLElement>("[data-popup-form]");

  popupContainers.forEach((container) => {
    // Пропускаем контейнеры, которые уже инициализированы
    if (container.dataset.reactInitialized === "true") {
      return;
    }

    const formId = container.dataset.popupForm;
    const triggerId = container.dataset.triggerId;
    const title = container.dataset.title || "Форма";

    if (formId) {
      // Проверяем, есть ли уже root для этого контейнера
      let root = rootsMap.get(container);

      if (!root) {
        root = createRoot(container);
        rootsMap.set(container, root);
      }

      root.render(
        <PopupForm formId={formId} triggerId={triggerId} title={title} />
      );

      // Помечаем контейнер как инициализированный
      container.dataset.reactInitialized = "true";
    }
  });

  // Дополнительно: обрабатываем все кнопки с data-trigger-for="callback-form"
  // на случай, если форма уже инициализирована, но кнопки появились позже
  const callbackButtons = document.querySelectorAll<HTMLElement>(
    '[data-trigger-for="callback-form"]'
  );

  callbackButtons.forEach((button) => {
    // Пропускаем кнопки, которые уже обрабатываются DynamicForm
    if (
      button.dataset.dynamicFormInitialized === "true" ||
      button.dataset.reactInitialized === "true"
    ) {
      return;
    }

    if (!button.dataset.callbackHandler) {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Ищем форму и открываем её
        const formContainer = document.querySelector<HTMLElement>(
          '[data-popup-form="callback-form"]'
        );
        if (formContainer) {
          // Триггерим открытие через событие или прямой вызов
          const triggerBtn = document.getElementById("callback-btn");
          if (triggerBtn) {
            triggerBtn.click();
          } else {
            // Альтернативный способ - найти любую кнопку с правильным ID
            const anyTrigger = document.querySelector<HTMLElement>(
              '[data-trigger-for="callback-form"], #callback-btn, #callback-btn-bottom'
            );
            if (anyTrigger) {
              anyTrigger.click();
            }
          }
        }
      });
      button.dataset.callbackHandler = "true";
      button.dataset.popupFormInitialized = "true";
    }
  });
}
